import PropTypes from 'prop-types';
import { ProductsWeb } from './ProductsWeb.js';
import { ProductsPdf } from './ProductsPdf.js';
import React, { useMemo } from 'react';
import {
  defaultRefreshEffectCondition,
  useCatalogInfo,
  usePriceContextHeader,
  usePriceInfoContextHeader,
  usePriceProducts,
  useRefreshEffect,
  useRestApi
} from 'app/common/hooks';
import { PriceContext } from 'app/common/contexts/index';
import { merge } from 'lodash';
import { handleImageContent, useEFlyerQRCode } from '../../common';

const PRODUCTS_TEMPLATE_ID = '01K0VVT0PD04BYXBG0K41GTBBB';

const ProductsTemplate = props => {
  const { content, themeApplication } = props;

  const { callToAction, footer, introText, promoHeader } = content.fields || {};

  const productIds = Array.from({ length: 16 }, (_, i) => {
    const field = content.fields?.[`product${i + 1}`];
    return field && field.value && field.value.id;
  }).filter(Boolean);

  const {
    hasLoadedPriceLists,
    config,
    fetchResponse,
    fetchProducts,
    refreshCondition
  } = useFetchEFlyerProducts(productIds);

  useRefreshEffect(
    fetchProducts,
    { hasLoadedPriceLists, config },
    refreshCondition,
    hasLoadedPriceLists
  );

  const fetchedProducts = useMemo(() => {
    const list = fetchResponse?.products || [];
    const map = new Map();
    list.forEach(p => {
      if (p && (p.id || p.sku)) {
        const key = p.id || p.sku;
        map.set(key, p);
      }
    });
    return map;
  }, [fetchResponse]);

  const prices = usePriceProducts(
    Array.from(fetchedProducts?.values?.() || [])
  );

  const { qrUrl, qrImage } = useEFlyerQRCode();

  const context = {
    logoUrl: handleImageContent(themeApplication.logoAsset),
    promoHeader: promoHeader?.value || promoHeader,
    introText: introText?.value,
    callToAction: callToAction?.value,
    footer: footer?.value,
    productIds,
    prices,
    fetchedProducts,
    productsLoaded: !!fetchResponse,
    qrUrl: qrUrl,
    qrImage: qrImage,
    themeColors: themeApplication.primaryColor
  };

  const isPdf = location.pathname.includes('/pdf');

  return isPdf ? (
    <ProductsPdf context={context} />
  ) : (
    <ProductsWeb context={context} />
  );
};

function useFetchEFlyerProducts(productIds) {
  const productIdsCsv = React.useMemo(() => productIds.join(','), [productIds]);

  const { productInfo: { fetchUrl, detailsUri } = {} } = useCatalogInfo();
  const detailsUrl = `${fetchUrl}${detailsUri}`;
  const { hasLoadedPriceLists } = React.useContext(PriceContext);
  const priceContextHeader = usePriceContextHeader();
  const priceInfoContextHeader = usePriceInfoContextHeader();

  const config = React.useMemo(
    () =>
      merge({}, priceContextHeader, priceInfoContextHeader, {
        params: {
          productIds: productIdsCsv,
          includePromos: false
        }
      }),
    [priceContextHeader, priceInfoContextHeader, productIdsCsv]
  );
  const {
    loading: fetchingProducts,
    error: fetchError,
    response: fetchResponse,
    sendCallback: fetchProducts
  } = useRestApi(detailsUrl, config, false);

  function refreshCondition(dependencies, prevDependencies) {
    return (
      defaultRefreshEffectCondition(dependencies, prevDependencies) &&
      dependencies.hasLoadedPriceLists &&
      !!productIdsCsv
    );
  }

  return {
    hasLoadedPriceLists,
    config,
    fetchResponse,
    fetchProducts,
    refreshCondition
  };
}

ProductsTemplate.propTypes = {
  content: PropTypes.shape({
    fields: PropTypes.object.isRequired
  }).isRequired,
  location: PropTypes.any
};

export default ProductsTemplate;
export { ProductsTemplate, PRODUCTS_TEMPLATE_ID };
